import React, { useMemo, useState } from "react";
import { consoleApi } from "../../services/consoleApiService";
import { useConsoleFetch } from "../../hooks/useConsoleFetch";
import ConsoleViewShell from "./ConsoleViewShell";
import BriefList from "./content-hub/BriefList";
import BriefForm from "./content-hub/BriefForm";
import BriefDetail from "./content-hub/BriefDetail";
import LibraryView from "./content-hub/LibraryView";
import ScreenshotsView from "./content-hub/ScreenshotsView";
import MediaView from "./content-hub/MediaView";
import "../../styles/console.css";
import "../../styles/console/console-content-hub.css";

const PILLAR_META = {
  "problem-reveal":     { label: "Problem → Reveal",  short: "Problem→Reveal", color: "#787f9a" },
  "feature-spotlight":  { label: "Feature Spotlight", short: "Feature",        color: "#d4a04c" },
  "club-spotlight":     { label: "Club Spotlight",    short: "Club",           color: "#c46b4e" },
  "comparison":         { label: "Comparison",        short: "Comparison",     color: "#3a6a4c" },
  "how-to":             { label: "How To",            short: "How-to",         color: "#5d6d83" },
};

const PILLAR_OPTIONS = [
  { value: "", label: "All pillars" },
  ...Object.entries(PILLAR_META).map(([value, meta]) => ({ value, label: meta.label })),
];

const PLATFORM_OPTIONS = [
  { value: "", label: "All platforms" },
  { value: "instagram", label: "Instagram" },
  { value: "facebook", label: "Facebook" },
  { value: "tiktok", label: "TikTok" },
];

const STATUS_OPTIONS = [
  { value: "", label: "All status" },
  { value: "draft", label: "Draft" },
  { value: "ready_to_render", label: "Ready" },
  { value: "rendered", label: "Rendered" },
  { value: "published", label: "Published" },
  { value: "archived", label: "Archived" },
];

const QUICK_STARTS = [
  {
    topic: "Feature spotlight: the Runcard",
    pillar: "feature-spotlight",
    platform: "instagram",
    slide_count: 5,
    target_persona: "solo-runner",
    blurb: "Show off the Runcard profile — pace, photos, bio.",
  },
  {
    topic: "You moved here six months ago. Still no run crew.",
    pillar: "problem-reveal",
    platform: "instagram",
    slide_count: 5,
    target_persona: "solo-runner",
    blurb: "The solo-runner pain hook → app reveal.",
  },
  {
    topic: "Club spotlight: Brickell Run Club",
    pillar: "club-spotlight",
    platform: "instagram",
    slide_count: 5,
    target_persona: "solo-runner",
    blurb: "Real club, real schedule, social proof.",
  },
  {
    topic: "Strava tracks the run. RunCards is how you got there.",
    pillar: "comparison",
    platform: "instagram",
    slide_count: 5,
    target_persona: "solo-runner",
    blurb: "Category-defining comparison post.",
  },
];

function IconCheck() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9.5" />
      <path d="M8.5 12.4l2.4 2.4 4.6-5" />
    </svg>
  );
}
function IconWarn() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 4 22 20 2 20 z" />
      <line x1="12" y1="11" x2="12" y2="15" />
      <circle cx="12" cy="17.5" r="0.6" fill="currentColor" />
    </svg>
  );
}
function IconArrow() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}
function IconSparkle() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

export default function ContentHubView() {
  const [filters, setFilters] = useState({ pillar: "", platform: "", status: "" });
  const [formOpen, setFormOpen] = useState(false);
  const [formInitial, setFormInitial] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [activeTab, setActiveTab] = useState("briefs"); // 'briefs' | 'library' | 'screenshots' | 'media'

  const listParams = useMemo(() => {
    const p = { limit: 200 };
    if (filters.pillar) p.pillar = filters.pillar;
    if (filters.platform) p.platform = filters.platform;
    if (filters.status) p.status = filters.status;
    return p;
  }, [filters]);

  const { data: list, loading, error, refetch } = useConsoleFetch(
    () => consoleApi.content.listBriefs(listParams).then((r) => (Array.isArray(r) ? r : [])),
    [listParams.pillar, listParams.platform, listParams.status],
    { refetchOnWindowFocus: false }
  );

  const { data: health } = useConsoleFetch(
    () => consoleApi.content.health(),
    [],
    { refetchOnWindowFocus: false }
  );

  const briefs = Array.isArray(list) ? list : [];

  const stats = useMemo(() => {
    const counts = { total: briefs.length, draft: 0, ready: 0, rendered: 0, published: 0 };
    briefs.forEach((b) => {
      if (b.status === "draft") counts.draft += 1;
      else if (b.status === "ready_to_render") counts.ready += 1;
      else if (b.status === "rendered") counts.rendered += 1;
      else if (b.status === "published") counts.published += 1;
    });
    return counts;
  }, [briefs]);

  const handleFilterChange = (key, value) => {
    setFilters((f) => ({ ...f, [key]: value }));
  };

  const handleCreate = async (body) => {
    const created = await consoleApi.content.createBrief(body);
    setFormOpen(false);
    setFormInitial(null);
    await refetch();
    if (created?.id) setSelectedId(created.id);
  };

  const openNewBrief = (initial = null) => {
    setFormInitial(initial);
    setFormOpen(true);
  };

  const showOnboarding = briefs.length === 0 && !filters.pillar && !filters.platform && !filters.status;

  return (
    <ConsoleViewShell loading={loading} error={error}>
      <div className="ch">
        {/* Header */}
        <header className="ch__hero">
          <div className="ch__hero-left">
            <div className="ch__eyebrow">RunCards · Marketing</div>
            <h1 className="ch__title">Content Hub</h1>
            <p className="ch__lede">
              Generate on-brand carousels for Instagram, Facebook, and TikTok.
              <span className="ch__lede-muted"> Claude writes the copy. Higgsfield handles the imagery. Playwright captures live screenshots. You ship.</span>
            </p>
          </div>
          <div className="ch__hero-right">
            <img src="/branding/logo-wordmark.png" alt="RunCards" className="ch__hero-logo" />
            <button
              type="button"
              className="ch__btn ch__btn--primary ch__cta"
              onClick={() => openNewBrief()}
            >
              New brief <span className="ch__btn-arrow"><IconArrow /></span>
            </button>
          </div>
        </header>

        {/* Status row */}
        <section className="ch__status">
          <SystemTile
            ok={health?.anthropic_key_set}
            label={health?.anthropic_key_set ? "Claude" : "Add API key"}
            sub={health?.anthropic_key_set ? "AI copy generation" : "ANTHROPIC_API_KEY missing"}
          />
          <SystemTile
            ok={health?.frontend_reachable}
            label={health?.frontend_reachable ? "Frontend" : "Frontend offline"}
            sub={health?.frontend_url || "Live screenshot capture"}
          />
          <StatTile num={stats.total}    label="Total briefs" />
          <StatTile num={stats.draft}    label="In draft" />
          <StatTile num={stats.ready}    label="Ready to render" />
          <StatTile num={stats.rendered} label="Rendered" tone="success" />
        </section>

        {showOnboarding && (
          <Onboarding onQuickStart={openNewBrief} quickStarts={QUICK_STARTS} />
        )}

        {/* Tab switcher */}
        <div className="ch__tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "briefs"}
            className={`ch__tab ${activeTab === "briefs" ? "is-active" : ""}`}
            onClick={() => setActiveTab("briefs")}
          >
            Briefs
            <span className="ch__tab-count">{stats.total}</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "library"}
            className={`ch__tab ${activeTab === "library" ? "is-active" : ""}`}
            onClick={() => setActiveTab("library")}
          >
            Library
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "screenshots"}
            className={`ch__tab ${activeTab === "screenshots" ? "is-active" : ""}`}
            onClick={() => setActiveTab("screenshots")}
          >
            Screenshots
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "media"}
            className={`ch__tab ${activeTab === "media" ? "is-active" : ""}`}
            onClick={() => setActiveTab("media")}
          >
            Media
          </button>
        </div>

        {activeTab === "library" ? (
          <LibraryView
            onOpenBrief={(id) => {
              setSelectedId(id);
              setActiveTab("briefs");
            }}
          />
        ) : activeTab === "screenshots" ? (
          <ScreenshotsView />
        ) : activeTab === "media" ? (
          <MediaView />
        ) : (
          <>
        {/* Filter bar */}
        <div className="ch__toolbar">
          <div className="ch__toolbar-left">
            <div className="ch__toolbar-title">
              <span className="ch__toolbar-eyebrow">Library</span>
              <span className="ch__toolbar-count">{stats.total} {stats.total === 1 ? "brief" : "briefs"}</span>
            </div>
          </div>
          <div className="ch__filters">
            <select
              className="ch__select"
              value={filters.pillar}
              onChange={(e) => handleFilterChange("pillar", e.target.value)}
            >
              {PILLAR_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <select
              className="ch__select"
              value={filters.platform}
              onChange={(e) => handleFilterChange("platform", e.target.value)}
            >
              {PLATFORM_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <select
              className="ch__select"
              value={filters.status}
              onChange={(e) => handleFilterChange("status", e.target.value)}
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            {(filters.pillar || filters.platform || filters.status) && (
              <button
                type="button"
                className="ch__btn ch__btn--link"
                onClick={() => setFilters({ pillar: "", platform: "", status: "" })}
              >
                Clear filters
              </button>
            )}
          </div>
        </div>

        <div className="ch__split">
          <div className="ch__list">
            <BriefList
              briefs={briefs}
              selectedId={selectedId}
              onSelect={setSelectedId}
              onRefresh={refetch}
            />
          </div>
          <div className="ch__detail">
            {selectedId ? (
              <BriefDetail
                briefId={selectedId}
                onChanged={() => refetch()}
                onOpenBrief={(id) => setSelectedId(id)}
                onDeleted={() => {
                  setSelectedId(null);
                  refetch();
                }}
              />
            ) : briefs.length > 0 ? (
              <DetailIdleState briefCount={briefs.length} onCreate={() => openNewBrief()} />
            ) : (
              <DetailWelcomeState onCreate={() => openNewBrief()} />
            )}
          </div>
        </div>
          </>
        )}

        {formOpen && (
          <BriefForm
            initial={formInitial}
            onClose={() => {
              setFormOpen(false);
              setFormInitial(null);
            }}
            onSubmit={handleCreate}
          />
        )}
      </div>
    </ConsoleViewShell>
  );
}

function SystemTile({ ok, label, sub }) {
  return (
    <div className={`ch__tile ch__tile--system ${ok ? "is-ok" : "is-warn"}`} title={sub}>
      <div className="ch__tile-ico">{ok ? <IconCheck /> : <IconWarn />}</div>
      <div className="ch__tile-text">
        <div className="ch__tile-num">{label}</div>
        <div className="ch__tile-label">{sub}</div>
      </div>
    </div>
  );
}

function StatTile({ num, label, tone = "default" }) {
  return (
    <div className={`ch__tile ch__tile--stat ch__tile--${tone}`}>
      <div className="ch__tile-text">
        <div className="ch__tile-num ch__tile-num--serif">{num}</div>
        <div className="ch__tile-label">{label}</div>
      </div>
    </div>
  );
}

function Onboarding({ onQuickStart, quickStarts }) {
  const steps = [
    { n: 1, title: "Write a brief", body: "Topic + pillar + platform. Claude needs ~10 words of intent." },
    { n: 2, title: "Generate copy", body: "Claude fills slide copy, captions, hashtags, and Higgsfield prompts." },
    { n: 3, title: "Add assets", body: "Upload Higgsfield outputs, attach stock screenshots, or capture live." },
    { n: 4, title: "Render to PNG", body: "Five publication-ready PNGs at 1080×1350 — schedule in Metricool." },
  ];
  return (
    <section className="ch__onboarding">
      <div className="ch__onboarding-grid">
        <div className="ch__onboarding-intro">
          <div className="ch__eyebrow ch__eyebrow--accent">Getting started</div>
          <h2 className="ch__onboarding-title">
            From idea to publishable carousel in <em>four steps</em>.
          </h2>
          <p className="ch__onboarding-sub">
            Every brief moves through the same pipeline. The system holds the brand voice; you bring the topic.
          </p>
        </div>
        <ol className="ch__steps">
          {steps.map((s) => (
            <li key={s.n} className="ch__step">
              <div className="ch__step-num">{s.n}</div>
              <div className="ch__step-text">
                <div className="ch__step-title">{s.title}</div>
                <div className="ch__step-body">{s.body}</div>
              </div>
            </li>
          ))}
        </ol>
      </div>
      <div className="ch__quickstart">
        <div className="ch__quickstart-head">
          <div className="ch__eyebrow ch__eyebrow--accent">Or start from a template</div>
          <span className="ch__quickstart-hint">Click to pre-fill the form — edit before saving</span>
        </div>
        <div className="ch__quickstart-grid">
          {quickStarts.map((q, i) => (
            <button
              key={i}
              type="button"
              className="ch__qs-card"
              onClick={() => onQuickStart(q)}
              style={{ "--qs-color": PILLAR_META[q.pillar]?.color || "var(--accent)" }}
            >
              <div className="ch__qs-card-stripe" />
              <div className="ch__qs-card-body">
                <div className="ch__qs-card-pillar">{PILLAR_META[q.pillar]?.short || q.pillar}</div>
                <div className="ch__qs-card-topic">{q.topic}</div>
                <div className="ch__qs-card-blurb">{q.blurb}</div>
              </div>
              <div className="ch__qs-card-arrow"><IconArrow /></div>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

function DetailWelcomeState({ onCreate }) {
  return (
    <div className="ch__detail-empty">
      <div className="ch__detail-empty-mark"><IconSparkle /></div>
      <div className="ch__eyebrow ch__eyebrow--accent">Empty library</div>
      <h3 className="ch__detail-empty-title">Make your first brief.</h3>
      <p className="ch__detail-empty-body">
        A brief is one carousel concept end-to-end. Pick a pillar, give Claude a topic,
        and the slides write themselves.
      </p>
      <button type="button" className="ch__btn ch__btn--primary" onClick={onCreate}>
        New brief <span className="ch__btn-arrow"><IconArrow /></span>
      </button>
    </div>
  );
}

function DetailIdleState({ briefCount, onCreate }) {
  return (
    <div className="ch__detail-empty">
      <div className="ch__detail-empty-mark">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="14" rx="2" />
          <line x1="3" y1="9" x2="21" y2="9" />
          <line x1="9" y1="14" x2="15" y2="14" />
        </svg>
      </div>
      <div className="ch__eyebrow ch__eyebrow--accent">{briefCount} {briefCount === 1 ? "brief" : "briefs"} in the library</div>
      <h3 className="ch__detail-empty-title">Pick one to edit, or start fresh.</h3>
      <p className="ch__detail-empty-body">
        Select a brief from the left to fill copy, attach assets, or render to PNG.
      </p>
      <button type="button" className="ch__btn ch__btn--ghost" onClick={onCreate}>
        New brief <span className="ch__btn-arrow"><IconArrow /></span>
      </button>
    </div>
  );
}
